const Colors = {
  DARK_RED: '#7F3844',
  CORAL: '#F76A7C',
  RED: '#D31868',
  ACTIVE_GREEN: '#31B07A',
  GREEN: '#42E6BB',
  RED_ORANGE: '#F57C00',
  PAUSED_ORANGE: '#F89F40',
  ORANGE: '#FFA300',
  BLACK: '#000',
  MUTED_BLACK: '#333',
  PRIMARY_PURPLE: '#715C8E',
  MUTED_PURPLE: '#765A92',
  BORDER_GREY: '#E0E0E0',
  BORDER_SMOKE: '#CFD1D4',
  BACKGROUND_GREY: '#BBBBBA',
  LIGHTEST_GREY: '#FAFAFA',
  LIGHT_GREY: '#8C8C8C',
  DARK_GREY: '#7F7F7F',
  WHITE: '#FFFFFF',
  FOCUS_BLUE: '#099BE8',
  BUTTON_GREY: '#D9D9D9',
  DARK_ORANGE: '#C9412B',
  DARK_BLUE: '#325B84',
  APPROVED_GREEN: '#006666',
  DISABLED_GREY: '#EEEEEE',
  ALLOY_BLUE: '#325B84',
  FADED_RED: '#f8d7da',
  FADED_GREEN: '#d4edda',
} as const;

const RebrandColors = {
  BLUE: '#5373C6',
  DEEP_DARK_GREY: '#464646',
  DEEP_GREY: '#8B8B82',
  DENIED: '#ED4E47',
  GREEN_300: '#6ED7AB',
  GREEN_400: '#59B18D',
  GREY: '#D9D9D9',
  GREY_900: '#464646',
  INK_BLACK: '#191919',
  INK_BLACK_DISABLED: '#D2D2D2',
  INK_BLACK_HOVERED: '#555555',
  LIGHT_GREEN: '#B8E1AA',
  LIGHT_YELLOW: '#C5C5B1',
  LINK: '#3374D6',
  NEUTRAL_GREY: '#CCCCC4',
  ORANGE_300: '#FDBA74',
  PINK: '#FF5260',
  PRODUCT_GREEN: '#91B200',
  PURPLE: '#B75DC6',
  PURPLE_LIGHT: '#E9CEEE',
  PURPLE_MEDIUM: '#D9AAE1',
  RED: '#D51010',
  RED_300: '#F67274',
  SKY: '#AACDE1',
  SMOKE_GREY: '#EBEBE6',
  SMOKE_GREY_DISABLED: '#F8F8F6',
  SMOKE_GREY_DISABLED_BORDER: '#F2F2F2',
  SMOKE_GREY_DISABLED_TEXT: '#AFAFAF',
  SMOKE_GREY_HOVERED: '#D8D8D3',
  SOFT_GREY: '#F5F5F3',
  WHITE: '#FFFFFF',
  ERROR_RED: '#FAEAE9',
  YELLOW_100: '#FBF4D3',
};

const GraphColors = {
  PURPLE: '#8264FF',
  HATCHING: '#C0B1FF',
};

const RiskLevels = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  ERROR: 'Error',
} as const;

const RiskToColors = {
  [RiskLevels.HIGH]: RebrandColors.RED_300,
  [RiskLevels.MEDIUM]: RebrandColors.ORANGE_300,
  [RiskLevels.LOW]: RebrandColors.GREEN_300,
  [RiskLevels.ERROR]: RebrandColors.NEUTRAL_GREY,
};

const RiskToSecondaryColors = {
  [RiskLevels.HIGH]: 'var(--cl-red-100)',
  [RiskLevels.MEDIUM]: 'var(--cl-orange-100)',
  [RiskLevels.LOW]: 'var(--cl-green-100)',
  [RiskLevels.ERROR]: 'var(--cl-grey-100)',
};

const Pagination = {
  CUSTOM_PAGE_LENS: [5, 10, 20],
  DEFAULT_PAGE_LEN: 10,
};

const PIITypes = {
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  DOB: 'Date of Birth',
  SSN: 'SSN',
  nationalID: 'National ID',
};

const MATRIX_MODEL = {
  STATUS: {
    EXECUTED: 'executed',
  },
  UNDEFINED_RESULT: 'Undefined Result',
};

const DOCUMENT_LICENSE_STATES = ['AL', 'AK', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY', 'AS', 'GU', 'MP', 'PR', 'UM', 'VI'];
const ALL_STATES = DOCUMENT_LICENSE_STATES.concat(['FM', 'MH', 'PW', 'AA', 'AE', 'AP']);
const COUNTRY_CODES = ['AC', 'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AN', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BU', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CP', 'CR', 'CS', 'CT', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DD', 'DE', 'DG', 'DJ', 'DK', 'DM', 'DO', 'DY', 'DZ', 'EA', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'EU', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FQ', 'FR', 'FX', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'HV', 'IC', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'JT', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MI', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NH', 'NI', 'NL', 'NO', 'NP', 'NQ', 'NR', 'NT', 'NU', 'NZ', 'OM', 'PA', 'PC', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PU', 'PW', 'PY', 'PZ', 'QA', 'RE', 'RH', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SU', 'SV', 'SX', 'SY', 'SZ', 'TA', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TP', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UK', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VD', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WK', 'WS', 'XK', 'YD', 'YE', 'YT', 'YU', 'ZA', 'ZM', 'ZR', 'ZW'];

// NOTE: These permission scopes are being re-used for non-Journey Application permissions.
//       Alter these with care.
const ASSIGN_APPLICATIONS_PERMISSION_SCOPES = {
  THEMSELVES: { value: 'themselves', label: 'To themselves' },
  EVERYONE: { value: 'everyone', label: 'To everyone' },
};

export const MAX_WORKFLOW_NAME_LENGTH = 64;
export const MAX_WORKFLOW_DESCRIPTION_LENGTH = 1000;

const WORKFLOW_TYPES = {
  PERSON: 'person',
  BUSINESS: 'business',
  TRANSACTION: 'transaction',
  DEVICE: 'device',
  RECURRING: 'webhook',
  ROUTER: 'journey_router',
  ROUTER_LEGACY: 'router',
  RECONCILIATION: 'reconciliation',
  PORTFOLIO: 'portfolio',
  GROUP: 'group',
} as const;

const WORKFLOW_USE_CASES = {
  ACCOUNT_OPENING: 'account_opening',
  CREDIT_UNDERWRITING: 'credit_underwriting',
  CREDIT_LINE_MONITORING: 'credit_line_monitoring',
  TRANSACTION_MONITORING: 'transaction_monitoring',
  ONGOING_EVENT_MONITORING: 'ongoing_event_monitoring',
  THIRD_PARTY_MONITORING: 'third_party_monitoring',
  THIRD_PARTY_BUSINESS_REGISTRY_MONITORING: 'third_party_business_registry_monitoring',
  ROUTER: 'router',
  LEGACY_ROUTER: 'legacy_router',
  GROUP_ASSESSMENT: 'group_assessment',
  ENTITY_FEEDBACK: 'entity_feedback',
} as const;

const WORKFLOW_OUTCOMES = {
  APPROVED: 'Approved',
  DENIED: 'Denied',
  MANUAL_REVIEW: 'Manual Review',
  ESCALATED: 'Escalated',
} as const;

const GRAY = {
  gray000: '#ffffff',
  gray100: '#FAFAF9',
  gray200: '#F5F5F3',
  gray300: '#EBEBE6',
  gray400: '#DEDED9',
  gray500: '#CCCCC4',
  gray600: '#A3A29B',
  gray700: '#7A7972',
  gray800: '#646461',
  gray900: '#464646',
  gray1000: '#333333',
  gray1100: '#191919',
};

const RED = {
  red100: '#FEE2E3',
  red200: '#FBA6A7',
  red300: '#F67274',
  red400: '#ED4649',
  red500: '#DA292E',
  red600: '#B71F23',
} as const;

const CHAMPION_CHALLENGER_COLORS = [
  '#41968d',
  '#C9412B',
  '#5A7B9C',
  '#FCC400',
  '#6A5B80',
  '#985f69',
];

const ONGOING_ALERTS_REBRAND = {
  ALERT_REVIEW: 'Review Needed',
  // if this is updated, please also update the "Investigations" text in compliance/templates/compliance/base.html
  APPLICATION: 'application',
};

const HOLISTIC_CREDIT_MLAS = {
  alh_credit_score: 'alh_credit_score',
  alh_days_since_last_eval: 'alh_days_since_last_eval',
  alh_days_since_last_ja: 'alh_days_since_last_ja',
  alh_fraud_score: 'alh_fraud_score',
  alh_latest_eval_outcome: 'alh_latest_eval_outcome',
  alh_latest_ja_outcome: 'alh_latest_ja_outcome',
  alh_outcome_reason: 'alh_outcome_reason',
  alh_reason_code: 'alh_reason_code',
  alh_review_reason: 'alh_review_reason',
};

// Policy LIbrary
const RULE_LIBRARY_ORGANIZATION_SLUG = 'rule-library';

/**
 * Note: There is a ticket (INFR-4534) to move the PRODUCT_UPDATES_* values into
 * Daffy environment secrets.
 *
 * Once this is done, we should remove these constants and use them from the
 * environment.
*/
const PRODUCT_UPDATES_URL = 'https://www.alloy.com/product-updates';
const PRODUCT_UPDATES_API_ENDPOINT = 'https://www.alloy.com/index.php/admin/actions/graphql/api';
const PRODUCT_UPDATES_API_HEADERS = {
  Authorization: 'Bearer 5BrN5MU1BKdG2QdcU_RvVPvU77IV5-s7',
};

export const RERUN_APPLICATION_FOOTER_CONTENT = 'There are fields that are missing and may cause the Application to error. Are you sure you want to run this application?';

const EVIDENCE_DATA_TYPES = {
  TRANSACTION: 'Transaction',
  BANK_ACCOUNT: 'Bank Account',
  CTR: 'CTR',
};

export {
  Colors,
  GraphColors,
  RebrandColors,
  RiskLevels,
  RiskToColors,
  RiskToSecondaryColors,
  Pagination,
  PIITypes,
  MATRIX_MODEL,
  DOCUMENT_LICENSE_STATES,
  ALL_STATES,
  COUNTRY_CODES,
  ASSIGN_APPLICATIONS_PERMISSION_SCOPES,
  WORKFLOW_TYPES,
  WORKFLOW_USE_CASES,
  WORKFLOW_OUTCOMES,
  GRAY,
  RED,
  CHAMPION_CHALLENGER_COLORS,
  ONGOING_ALERTS_REBRAND,
  HOLISTIC_CREDIT_MLAS,
  RULE_LIBRARY_ORGANIZATION_SLUG,
  PRODUCT_UPDATES_URL,
  PRODUCT_UPDATES_API_ENDPOINT,
  PRODUCT_UPDATES_API_HEADERS,
  EVIDENCE_DATA_TYPES,
};
